import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Minus, Plus, ShoppingBag, Trash2, Truck, MapPin } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CartDrawer = () => {
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity, clearCart, subtotal, isCartOpen, setIsCartOpen } = useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState('cart'); // 'cart', 'details'
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    fulfillmentType: 'pickup',
    deliveryAddress: '',
    notes: ''
  });

  const handleCheckout = async () => {
    if (items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    // Validate customer info
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (customerInfo.fulfillmentType === 'delivery' && !customerInfo.deliveryAddress) {
      toast.error('Please enter a delivery address');
      return;
    }

    setIsCheckingOut(true);

    try {
      const response = await fetch(`${API_URL}/api/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items.map(item => ({
            product_id: item.product_id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            size: item.size
          })),
          customer_name: customerInfo.name,
          customer_email: customerInfo.email,
          customer_phone: customerInfo.phone,
          fulfillment_type: customerInfo.fulfillmentType,
          delivery_address: customerInfo.fulfillmentType === 'delivery' ? customerInfo.deliveryAddress : null,
          notes: customerInfo.notes || null,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setIsCheckingOut(false);
    }
  };

  if (!isCartOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={() => setIsCartOpen(false)}
      />
      
      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <ShoppingBag size={24} />
            <h2 className="text-xl font-bold" style={{ fontFamily: "'Crimson Text', serif" }}>
              {checkoutStep === 'cart' ? 'Your Cart' : 'Checkout'}
            </h2>
          </div>
          <button 
            onClick={() => setIsCartOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {checkoutStep === 'cart' ? (
            <>
              {items.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag className="mx-auto text-gray-300 mb-4" size={64} />
                  <p className="text-gray-500">Your cart is empty</p>
                  <Button 
                    onClick={() => {
                      setIsCartOpen(false);
                      navigate('/nursery-shop');
                    }}
                    className="mt-4 bg-black hover:bg-gray-800"
                  >
                    Browse Plants
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item, index) => (
                    <div key={`${item.product_id}-${item.size}-${index}`} className="flex gap-4 bg-gray-50 rounded-lg p-3">
                      {item.image && (
                        <img 
                          src={item.image} 
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                      )}
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm">{item.name}</h4>
                        {item.size && (
                          <p className="text-xs text-gray-500">{item.size}</p>
                        )}
                        <p className="text-emerald-700 font-bold">${item.price.toFixed(2)}</p>
                        
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={() => updateQuantity(item.product_id, item.size, item.quantity - 1)}
                            className="p-1 bg-gray-200 rounded hover:bg-gray-300"
                          >
                            <Minus size={14} />
                          </button>
                          <span className="w-8 text-center text-sm">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.product_id, item.size, item.quantity + 1)}
                            className="p-1 bg-gray-200 rounded hover:bg-gray-300"
                          >
                            <Plus size={14} />
                          </button>
                          <button
                            onClick={() => removeItem(item.product_id, item.size)}
                            className="ml-auto p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <button
                    onClick={clearCart}
                    className="text-sm text-red-500 hover:underline"
                  >
                    Clear Cart
                  </button>
                </div>
              )}
            </>
          ) : (
            /* Checkout Details Form */
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                  placeholder="Your full name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                  placeholder="your@email.com"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Phone *</label>
                <input
                  type="tel"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Fulfillment Method *</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setCustomerInfo({...customerInfo, fulfillmentType: 'pickup'})}
                    className={`p-4 border-2 rounded-lg flex flex-col items-center gap-2 transition-all ${
                      customerInfo.fulfillmentType === 'pickup'
                        ? 'border-emerald-600 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <MapPin size={24} className={customerInfo.fulfillmentType === 'pickup' ? 'text-emerald-600' : 'text-gray-400'} />
                    <span className="font-medium text-sm">Farm Pickup</span>
                    <span className="text-xs text-gray-500">Free</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomerInfo({...customerInfo, fulfillmentType: 'delivery'})}
                    className={`p-4 border-2 rounded-lg flex flex-col items-center gap-2 transition-all ${
                      customerInfo.fulfillmentType === 'delivery'
                        ? 'border-emerald-600 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Truck size={24} className={customerInfo.fulfillmentType === 'delivery' ? 'text-emerald-600' : 'text-gray-400'} />
                    <span className="font-medium text-sm">Local Delivery</span>
                    <span className="text-xs text-gray-500">Contact for fee</span>
                  </button>
                </div>
              </div>
              
              {customerInfo.fulfillmentType === 'delivery' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Delivery Address *</label>
                  <textarea
                    value={customerInfo.deliveryAddress}
                    onChange={(e) => setCustomerInfo({...customerInfo, deliveryAddress: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                    placeholder="Enter your delivery address"
                    rows={3}
                    required
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-1">Special Notes</label>
                <textarea
                  value={customerInfo.notes}
                  onChange={(e) => setCustomerInfo({...customerInfo, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                  placeholder="Any special requests or instructions"
                  rows={2}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t p-4 bg-gray-50">
            <div className="flex justify-between mb-4">
              <span className="font-medium">Subtotal</span>
              <span className="font-bold text-lg">${subtotal.toFixed(2)}</span>
            </div>
            
            {checkoutStep === 'cart' ? (
              <Button
                onClick={() => setCheckoutStep('details')}
                className="w-full bg-black hover:bg-gray-800 text-white py-3"
              >
                Proceed to Checkout
              </Button>
            ) : (
              <div className="space-y-2">
                <Button
                  onClick={handleCheckout}
                  disabled={isCheckingOut}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-3"
                >
                  {isCheckingOut ? 'Processing...' : `Pay $${subtotal.toFixed(2)}`}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setCheckoutStep('cart')}
                  className="w-full"
                  disabled={isCheckingOut}
                >
                  Back to Cart
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
